import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import StreakCelebration from 'src/components/Streak/celebration/StreakCelebration';
import StreakProtectedPanel from 'src/components/DailyStreakCheckin/StreakProtectedPanel';
import useStreakCelebration from 'src/components/Streak/celebration/hooks/useStreakCelebration';
import { useStreakDayRecorded } from 'src/hooks/useStreakDayRecorded';
import { useCurrentUserId } from 'src/queries/streakGoal';
import { ROUTES } from 'src/config/routes';
import { useStreakCheckinMutation } from 'src/queries/streakCheckin';
import { useGrantStarterFreeze, useStreakSummary } from 'src/queries/streak';
import type { StreakCheckinStreakBlock } from 'src/types/streak.types';
import { reportError } from 'src/modules/logs/Sentry';
import { readStoredItem, removeStoredItem, writeStoredItem } from 'src/utils/storageManager';
import { toDayKey } from 'src/utils/date';
import { subscribeAppForeground } from 'src/modules/appLifecycle';

export const STREAK_CHECKIN_STORAGE_KEY = 'streak-checkin-date';
export const STREAK_STARTER_STORAGE_KEY = 'streak-starter-granted';

/** Scoped per child: siblings share the device and nothing clears localStorage on switch. */
export const starterStorageKey = (userId: number) =>
  `${STREAK_STARTER_STORAGE_KEY}:${userId}`;

export const checkinStorageKey = (userId: number) =>
  `${STREAK_CHECKIN_STORAGE_KEY}:${userId}`;

export const DailyStreakCheckin = () => {
  const { mutate: checkin } = useStreakCheckinMutation();
  const pendingForRef = useRef<number | undefined>(undefined);
  const [hasRecordedADay, setHasRecordedADay] = useState(false);
  const [save, setSave] = useState<StreakCheckinStreakBlock | null>(null);

  const { mutate: grantStarter } = useGrantStarterFreeze();
  const requestedStarterForRef = useRef<number | undefined>(undefined);
  const userId = useCurrentUserId();

  const isDayRecorded = useStreakDayRecorded(userId);
  const freezes = useStreakSummary().data?.freezes;

  const { pathname } = useLocation();
  const isHeld = pathname.replace(/\/$/, '') !== ROUTES.CHILD_HOME.path;

  const shownForUserRef = useRef<number | undefined>(undefined);
  const [shownForUserId, setShownForUserId] = useState<number | undefined>(undefined);
  if (userId !== undefined && shownForUserId !== userId) {
    setShownForUserId(userId);
    if (shownForUserId !== undefined) setSave(null);
  }
  useEffect(() => {
    if (userId === undefined) return;
    shownForUserRef.current = userId;
  }, [userId]);
  const { variant, streakLength, goal, dismiss } = useStreakCelebration({
    isArmed: (hasRecordedADay || isDayRecorded) && !isHeld,
  });

  useEffect(() => {
    if (userId === undefined) return;

    const forUser = userId;

    const maybeCheckin = () => {
      if (pendingForRef.current === forUser) return;
      if (readStoredItem(checkinStorageKey(forUser)) === toDayKey()) {
        return;
      }

      pendingForRef.current = forUser;
      checkin(undefined, {
        onSuccess: data => {
          writeStoredItem(checkinStorageKey(forUser), toDayKey());
          removeStoredItem(STREAK_CHECKIN_STORAGE_KEY);
          if (shownForUserRef.current !== forUser) return;
          if (data.created) setHasRecordedADay(true);
          if (data.streak?.saved) setSave(data.streak);
        },
        onSettled: () => {
          if (pendingForRef.current === forUser) {
            pendingForRef.current = undefined;
          }
        },
      });
    };

    maybeCheckin();

    return subscribeAppForeground(maybeCheckin, { isVisibilityIncluded: true });
  }, [userId, checkin]);

  useEffect(() => {
    if (userId === undefined) return;
    if (requestedStarterForRef.current === userId) return;

    if (readStoredItem(starterStorageKey(userId)) === '1') return;

    requestedStarterForRef.current = userId;
    grantStarter(undefined, {
      onSuccess: () => {
        writeStoredItem(starterStorageKey(userId), '1');
      },
      onError: error => {
        requestedStarterForRef.current = undefined;
        reportError('Failed to grant starter freeze', error);
      },
    });
  }, [userId, grantStarter]);

  return (
    <>
      {variant !== null && (
        <StreakCelebration
          variant={variant}
          streakLength={streakLength}
          goalDays={goal?.targetDays}
          rewardBabz={goal?.rewardBabz}
          onContinue={dismiss}
        />
      )}
      {!isHeld && (
        <StreakProtectedPanel
          isOpen={variant === null && save !== null}
          onClose={() => setSave(null)}
          saved={save?.saved ?? null}
          streakLength={save?.current_length ?? 0}
          freezesLeft={freezes?.held ?? save?.saved?.freezes_left ?? 0}
          freezes={freezes}
        />
      )}
    </>
  );
};
